<template>
  <v-container class="prompt-queue-page" fluid>
    <header class="page-header">
      <div>
        <h1 class="page-title">Prompt Proposals</h1>
        <p class="page-subtitle">
          Agent-generated proposals awaiting review. Approve to apply the new prompt to the client's
          <code class="mono">ClientCompanyConfigValue</code>.
        </p>
      </div>
      <v-btn :to="{ name: 'prompt-tuning-triage' }" prepend-icon="mdi-filter-variant" variant="outlined">
        Triage queue
      </v-btn>
    </header>

    <section class="filter-bar">
      <v-select
        v-model="filters.status"
        :items="statusOptions"
        label="Status"
        clearable
        density="compact"
        hide-details
        variant="outlined" />
      <v-select
        v-model="filters.configKey"
        :items="configKeyOptions"
        item-title="label"
        item-value="value"
        label="Prompt"
        clearable
        density="compact"
        hide-details
        variant="outlined" />
    </section>

    <v-data-table-server
      :headers="headers"
      :items="rows"
      :items-length="totalRows"
      :loading="loading"
      :page="page"
      :items-per-page="pageSize"
      class="queue-table"
      @update:page="onPageChange"
      @update:items-per-page="onPageSizeChange"
      @click:row="openProposal">
      <template #[`item.status`]="{ item }">
        <span class="status-chip" :class="`status-chip--${item.status.toLowerCase()}`">
          {{ item.status }}
        </span>
      </template>
      <template #[`item.config_key`]="{ item }">
        {{ configKeyLabel(item.configKey) }}
      </template>
      <template #[`item.attempts`]="{ item }">
        {{ item.attempts }}
      </template>
      <template #[`item.gate`]="{ item }">
        <v-icon v-if="item.gatePassed" color="success">mdi-check-circle</v-icon>
        <v-icon v-else color="error">mdi-close-circle-outline</v-icon>
      </template>
      <template #[`item.created_on`]="{ item }">
        <span class="muted">{{ relTime(item.createdOn) }}</span>
      </template>
    </v-data-table-server>
  </v-container>
</template>

<script setup>
/* eslint-disable no-console -- error surfacing is via console for v1; UI snackbar TBD */
import { onMounted, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { promptTuningProposals } from "@/services/promptTuningService";
import { CONFIG_KEYS, PROPOSAL_STATUSES, configKeyLabel } from "@/services/promptTuningConstants";
import { timeAgoString } from "@/utils";

const router = useRouter();

const rows = ref([]);
const totalRows = ref(0);
const loading = ref(false);
const page = ref(1);
const pageSize = ref(50);

const filters = reactive({
  status: null,
  configKey: null,
});

const statusOptions = PROPOSAL_STATUSES;
const configKeyOptions = CONFIG_KEYS;

const headers = [
  { title: "Status", key: "status", width: 140 },
  { title: "Client", key: "clientCompanyName" },
  { title: "Prompt", key: "config_key" },
  { title: "Attempts", key: "attempts", width: 100 },
  { title: "Gate", key: "gate", width: 80, sortable: false },
  { title: "Created", key: "created_on", width: 140 },
];

function relTime(iso) {
  if (!iso) return "";
  // `timeAgoString` is the canonical relative-time formatter (`frontend/src/utils/index.js`).
  return `${timeAgoString(new Date(iso))} ago`;
}

async function load() {
  loading.value = true;
  try {
    const params = { page: page.value, page_size: pageSize.value };
    if (filters.status) params.status = filters.status;
    if (filters.configKey) params.config_key = filters.configKey;
    const response = await promptTuningProposals.list(params);
    rows.value = response.results || [];
    totalRows.value = response.count || 0;
  } catch (err) {
    console.error("Failed to load proposals", err);
  } finally {
    loading.value = false;
  }
}

function openProposal(_event, { item }) {
  router.push({ name: "prompt-tuning-detail", params: { id: item.id } });
}

function onPageChange(p) {
  page.value = p;
  load();
}

function onPageSizeChange(s) {
  pageSize.value = s;
  page.value = 1;
  load();
}

watch(
  filters,
  () => {
    page.value = 1;
    load();
  },
  { deep: true }
);
onMounted(load);
</script>

<style lang="scss" scoped>
.prompt-queue-page {
  padding: 24px 32px;
  max-width: 1400px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.page-title {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0;
}

.page-subtitle {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  margin: 6px 0 0;
  max-width: 720px;
}

.filter-bar {
  display: grid;
  grid-template-columns: 200px 240px;
  gap: 12px;
  margin-bottom: 16px;
}

.queue-table {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: #ffffff;

  :deep(tbody tr) {
    cursor: pointer;
  }
}

.mono {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 12px;
}

.muted {
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
}

.status-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;

  &--pending {
    background: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.7);
  }
  &--low_confidence {
    background: rgba(255, 165, 0, 0.15);
    color: rgb(180, 110, 0);
  }
  &--approved {
    background: rgba(34, 139, 230, 0.12);
    color: rgb(20, 110, 200);
  }
  &--applied {
    background: rgba(46, 160, 67, 0.15);
    color: rgb(30, 130, 60);
  }
  &--rejected {
    background: rgba(212, 70, 70, 0.12);
    color: rgb(180, 50, 50);
  }
}
</style>
